import React, { useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import {
  Body1,
  Body1Strong,
  Caption1Stronger,
  Menu,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Tag,
} from '@fluentui/react-components';
import BarChartIcon from '@mui/icons-material/BarChart';
import { FormattedMessage, useIntl } from 'react-intl';
import { chart_list, setPendingChartLoadTrigger } from '@55degrees/layout-r18';
import { useLocation } from 'react-router';
import { onlyUnique } from './helper';
import {
  CustomMenuButton,
  CustomMenuGroup,
  CustomMenuGroupHeader,
  CustomMenuItem,
  MenuContainer,
  MenuLabelWrapper,
} from './styling';
import ChartsDropdownIcons from './ChartsDropdownIcons';
import { ProductFruitsReporter } from '../../Helpers/ProductFruitsInit';
import { A3DocumentContext } from '../../../contexts/A3DocumentContext';

function ChartsMenu({ isDialogOpen, isNormalScreenSize }) {
  const location = useLocation();
  const intl = useIntl();
  const isIndexPage = location.pathname === '/';
  const { activeChart, setActiveChart } = useContext(A3DocumentContext);

  const groups = chart_list.map((page) => page.group).filter(onlyUnique);

  const keys = chart_list.map((page) => page.hot_key);

  const isProductFruitFocusedInput = (event) => {
    return event
      .composedPath()
      .some((el) => el.classList && el.classList.contains('productfruits--container'));
  };

  const handleKeyDown = (event) => {
    if (!window.$ALLOW_KEY_BIND || isDialogOpen) return;
    if (isProductFruitFocusedInput(event)) return;
    if (keys.includes(event.key) && !event.metaKey && !event.ctrlKey) {
      const pageIndex = chart_list.findIndex((element) => element.hot_key === event.key);
      setPendingChartLoadTrigger('Shortcut');
      setActiveChart(chart_list[pageIndex].id);
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isDialogOpen]);

  const emptyFirst = (a, b) => (a && b ? !a - !b : a.localeCompare(b));

  const currentChartName = chart_list.filter((c) => {
    return c.id === activeChart;
  })[0]?.name;

  const onChartClick = (chart) => {
    ProductFruitsReporter.track(`load${chart.name.replace(/\s+/g, '')}`);
    setPendingChartLoadTrigger('Dropdown');
    setActiveChart(chart.id);
  };

  const generateGroupLabel = (group) => {
    if (group === 'key flow data') {
      return 'Key flow data';
    }
    if (group === 'process stability insights') {
      return 'Process stability insights';
    }
    if (group === 'forecasting simulations') {
      return 'Forecasting simulations';
    }
    if (group === 'other') {
      return 'Other';
    }

    return group;
  };

  const innerLabelName = !isIndexPage
    ? currentChartName
    : intl.formatMessage({
        id: 'landing.page.chart.dropdown.inner.label',
        defaultMessage: 'No chart selected',
      });

  const chartList = groups.sort(emptyFirst).map((group, index) => (
    <CustomMenuGroup key={`chartsdropdown-group-${group}`}>
      {index !== 0 && <CustomMenuGroupHeader>{generateGroupLabel(group)}</CustomMenuGroupHeader>}
      {chart_list
        .filter((page) => page.group === group)
        .map((chart) => (
          <CustomMenuItem
            key={`chartsdropdown-item-${group}-${chart.id}`}
            disabled={activeChart === chart.id}
            icon={<ChartsDropdownIcons name={chart.name} isSelected={activeChart === chart.id} />}
            onClick={() => onChartClick(chart)}
            secondaryContent={chart.hot_key}
            isUncategorized={index === 0}
          >
            <Body1>{chart.name}</Body1>
            {chart.is_beta ? (
              <>
                {' '}
                <Tag shape="circular" size="extra-small" appearance="brand">
                  BETA
                </Tag>
              </>
            ) : null}
          </CustomMenuItem>
        ))}
    </CustomMenuGroup>
  ));

  return (
    <MenuContainer>
      <MenuLabelWrapper>
        {isNormalScreenSize ? (
          <Caption1Stronger>
            <FormattedMessage
              id="chart.aside.label"
              defaultMessage="CHARTS"
              description="Text Aside of Menu"
            />
          </Caption1Stronger>
        ) : (
          <BarChartIcon fontSize="small" />
        )}
      </MenuLabelWrapper>
      <Menu hasIcons>
        <MenuTrigger disableButtonEnhancement>
          <CustomMenuButton id="a3-ch-dropdown" disabled={isIndexPage}>
            <Body1Strong>{innerLabelName}</Body1Strong>
          </CustomMenuButton>
        </MenuTrigger>
        <MenuPopover>
          <MenuList>{chartList}</MenuList>
        </MenuPopover>
      </Menu>
    </MenuContainer>
  );
}

ChartsMenu.propTypes = {
  isDialogOpen: PropTypes.bool.isRequired,
  isNormalScreenSize: PropTypes.bool.isRequired,
};

export default ChartsMenu;
